import { AxiosError } from "axios";

export type TimeReportErrorKey =
  | "Alerts.CannotModifyClosedTimeReport"
  | "Alerts.CannotWithdrawTimeReportClosedApproved"
  | "Alerts.CannotReopenTimeReport"
  | "Alerts.TimeReportNotFound"
  | "Alerts.UnexpectedError";

export type TimeReportBulkErrorKey =
  | "CompanyTimeReport.CannotModifyClosedTimeReport"
  | "CompanyTimeReport.CannotWithdrawTimeReportClosedApproved"
  | "CompanyTimeReport.CannotReopenTimeReport"
  | "CompanyTimeReport.TimeReportNotFound"
  | "CompanyTimeReport.UnexpectedError";

export interface TimeReportBackendErrorResponse {
  errorCode: number;
  errorMessage: string;
  errorType: string;
  metadata: object;
  referenceId: string;
}

const MESSAGE_TO_ALERT_KEY: Record<string, TimeReportErrorKey> = {
  "Cannot modify closed time report": "Alerts.CannotModifyClosedTimeReport",
  "Cannot withdraw time report if already closed and approved":
    "Alerts.CannotWithdrawTimeReportClosedApproved",
  "Cannot reopen this time report": "Alerts.CannotReopenTimeReport",
  "Time report not found": "Alerts.TimeReportNotFound",
};

const resolveApiError = (
  error: AxiosError<TimeReportBackendErrorResponse>,
): { status?: number; errorMessage?: string } => {
  const status = error.response?.status ?? error.response?.data?.errorCode;
  const errorMessage = error.response?.data?.errorMessage;
  return { status, errorMessage };
};

export const resolveAlertKey = (message: string): TimeReportErrorKey =>
  MESSAGE_TO_ALERT_KEY[message] ?? "Alerts.UnexpectedError";

export const resolveAlertKeyFromApiError = (
  error: AxiosError<TimeReportBackendErrorResponse>,
): TimeReportErrorKey => {
  const { status, errorMessage } = resolveApiError(error);
  if (typeof status === "number" && status >= 500) {
    return "Alerts.UnexpectedError";
  }
  if (errorMessage) {
    return resolveAlertKey(errorMessage);
  }
  return "Alerts.UnexpectedError";
};

export const resolveBulkErrorKeyFromApiError = (
  error: AxiosError<TimeReportBackendErrorResponse>,
): TimeReportBulkErrorKey => toBulkErrorKey(resolveAlertKeyFromApiError(error));

export const toBulkErrorKey = (
  alertKey: TimeReportErrorKey,
): TimeReportBulkErrorKey => {
  switch (alertKey) {
    case "Alerts.CannotModifyClosedTimeReport":
      return "CompanyTimeReport.CannotModifyClosedTimeReport";
    case "Alerts.CannotWithdrawTimeReportClosedApproved":
      return "CompanyTimeReport.CannotWithdrawTimeReportClosedApproved";
    case "Alerts.CannotReopenTimeReport":
      return "CompanyTimeReport.CannotReopenTimeReport";
    case "Alerts.TimeReportNotFound":
      return "CompanyTimeReport.TimeReportNotFound";
    case "Alerts.UnexpectedError":
      return "CompanyTimeReport.UnexpectedError";
  }
};
